/**
 * Passive, non-blocking notices.
 *
 * Deliberately NOT built on popup.ts: both of those are blocking modals that
 * steal focus and clear each other. Cart sync needs to say "your cart changed
 * on another device" without interrupting someone mid-order.
 *
 * Plain DOM + inline styles, no jQuery and no stylesheet dependency, so the
 * same module works in the site-wide cart-sync bundle where neither is
 * guaranteed to be present.
 */

const CONTAINER_ID = "wp-toast-container";
const DEFAULT_DURATION_MS = 6000;

/**
 * Where a toast stack sits.
 *
 * Bottom-left is the original and stays the default: cart sync's notices are
 * ambient and must not cover the cart's own controls. The portal opts into
 * top-right for payment confirmations, which are consequential enough to want
 * the eye. Each position gets its OWN container — cart-sync.js also loads on a
 * proxy page, so both stacks can be live at once and must not fight.
 */
export const ToastPosition = {
  BottomLeft: "bottom-left",
  TopRight: "top-right",
} as const;
export type ToastPosition = (typeof ToastPosition)[keyof typeof ToastPosition];

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastOptions {
  /** Milliseconds before auto-dismiss. 0 = persist until dismissed. */
  readonly durationMs?: number;
  /** Optional single action button (e.g. "Restore"). */
  readonly action?: ToastAction;
  /**
   * Replaces any existing toast with the same key instead of stacking, so a
   * repeated sync event cannot pile up notices.
   */
  readonly key?: string;
  /** Defaults to bottom-left, which is what every existing caller expects. */
  readonly position?: ToastPosition;
  /**
   * Put the action row BENEATH the message instead of beside it.
   *
   * ⚠ Opt-in, because the default single-row layout is what every existing caller was
   * written against. It exists for consequential, sentence-length messages: the row layout
   * caps the toast at 380px and gives the buttons a fixed width, so a payment confirmation
   * gets squeezed into roughly 180px and wraps to one or two words per line. Short ambient
   * notices ("Cart updated") are unaffected and stay on one line.
   */
  readonly stackAction?: boolean;
}

function ensureContainer(position: ToastPosition): HTMLElement {
  const id = position === ToastPosition.TopRight ? `${CONTAINER_ID}-top-right` : CONTAINER_ID;
  const existing = document.getElementById(id);
  if (existing) return existing;

  const container = document.createElement("div");
  container.id = id;
  container.setAttribute("role", "status");
  container.setAttribute("aria-live", "polite");
  container.style.cssText = [
    "position:fixed",
    "z-index:2147483000",
    position === ToastPosition.TopRight ? "top:16px" : "bottom:16px",
    position === ToastPosition.TopRight ? "right:16px" : "left:16px",
    "display:flex",
    "flex-direction:column",
    "gap:8px",
    // ⚠ The TOP-RIGHT container is the Customer Portal's, and its notices are sentences —
    // a payment confirmation naming an order, an amount and an email address. The
    // bottom-left default stays at 380px because cart sync's notices are three words.
    position === ToastPosition.TopRight
      ? "max-width:min(460px, calc(100vw - 32px))"
      : "max-width:min(380px, calc(100vw - 32px))",
    "pointer-events:none",
  ].join(";");

  document.body.appendChild(container);
  return container;
}

/**
 * Show a passive notice. Returns a dismiss function so a caller holding a
 * persistent toast (e.g. "restore your cart") can take it down once the
 * situation resolves.
 */
export function showToast(message: string, options: ToastOptions = {}): () => void {
  const container = ensureContainer(options.position ?? ToastPosition.BottomLeft);

  if (options.key) {
    container
      .querySelectorAll(`[data-toast-key="${CSS.escape(options.key)}"]`)
      .forEach((el) => el.remove());
  }

  const toast = document.createElement("div");
  if (options.key) toast.dataset.toastKey = options.key;
  toast.style.cssText = [
    "pointer-events:auto",
    "background:#230051",
    "color:#ffffff",
    "border-radius:6px",
    "padding:12px 14px",
    "font-size:14px",
    "line-height:1.4",
    "box-shadow:0 4px 16px rgba(0,0,0,0.25)",
    "display:flex",
    // The container caps at 380px; a stacked toast carries a sentence, so let it use the
    // full width the viewport allows rather than inheriting the ambient-notice ceiling.
    options.stackAction === true ? "width:min(460px, calc(100vw - 32px))" : "",
    // Stacked: message on its own full-width line, buttons beneath and right-aligned.
    options.stackAction === true ? "flex-direction:column" : "flex-direction:row",
    options.stackAction === true ? "align-items:stretch" : "align-items:center",
    "gap:12px",
    "opacity:0",
    "transform:translateY(8px)",
    "transition:opacity 180ms ease-out, transform 180ms ease-out",
  ].join(";");

  const text = document.createElement("span");
  // `min-width:0` lets a long word wrap instead of forcing the flex item wider than its
  // share; without it a single long token can push the buttons off the toast.
  text.style.cssText = "flex:1 1 auto;min-width:0";
  text.textContent = message;
  toast.appendChild(text);

  let dismissed = false;
  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timer);
    toast.style.opacity = "0";
    toast.style.transform = "translateY(8px)";
    window.setTimeout(() => toast.remove(), 200);
  }

  // When stacking, the two controls share a right-aligned row beneath the message rather
  // than competing with it for width.
  const actionRow = document.createElement("div");
  if (options.stackAction === true) {
    actionRow.style.cssText = "display:flex;align-items:center;justify-content:flex-end;gap:8px";
  }
  const controls: HTMLElement = options.stackAction === true ? actionRow : toast;

  if (options.action) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = options.action.label;
    button.style.cssText = [
      "flex:0 0 auto",
      "background:#ffffff",
      "color:#230051",
      "border:0",
      "border-radius:4px",
      "padding:6px 10px",
      "font-size:13px",
      "font-weight:600",
      "cursor:pointer",
    ].join(";");
    button.addEventListener("click", () => {
      dismiss();
      options.action?.onClick();
    });
    controls.appendChild(button);
  }

  const close = document.createElement("button");
  close.type = "button";
  close.setAttribute("aria-label", "Dismiss");
  close.textContent = "×";
  close.style.cssText = [
    "flex:0 0 auto",
    "background:transparent",
    "color:#ffffff",
    "border:0",
    "font-size:18px",
    "line-height:1",
    "cursor:pointer",
    "opacity:0.7",
  ].join(";");
  close.addEventListener("click", dismiss);
  controls.appendChild(close);
  if (options.stackAction === true) toast.appendChild(actionRow);

  container.appendChild(toast);

  // Next frame so the entry transition actually runs.
  window.requestAnimationFrame(() => {
    toast.style.opacity = "1";
    toast.style.transform = "translateY(0)";
  });

  const duration = options.durationMs ?? DEFAULT_DURATION_MS;
  const timer =
    duration > 0 ? window.setTimeout(dismiss, duration) : 0;

  return dismiss;
}
